"use client"

import dynamic from "next/dynamic"
import Image from "next/image"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { useCountry } from "@/components/layout/country-provider"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { bookingUrlForCountry } from "@/lib/booking"
import { cmsLocale, type Testimonial } from "@/lib/cms"
import { EditableField } from "@/components/editor/editable-field"
import { FaqSection } from "@/components/ui/faq-section"
import { TestimonialCardsRow } from "@/components/ui/testimonial-cards-row"
import { InsuranceSection } from "@/components/ui/insurance-section"
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowUpRightIcon,
} from "@/components/ui/icons"
import {
  ClinicAddressHours,
  ClinicLanguageNote,
  ClinicParking,
  ClinicServices,
  ClinicTransport,
  type ClinicDetailData,
} from "./clinic-sections"

const SingleLocationMap = dynamic(
  () =>
    import("@/components/ui/single-location-map").then((m) => ({
      default: m.SingleLocationMap,
    })),
  {
    ssr: false,
    loading: () => (
      <div
        className="w-full rounded-[20px] bg-neutral-100"
        style={{ height: 400 }}
      />
    ),
  }
)

// ─── Types ────────────────────────────────────────────────

interface DeepClinic {
  id: string
  clinic: string
  clinicSlug: string
  address: string
  type: "mri" | "blood"
  lat: number
  lng: number
  image: string | null
  detail: ClinicDetailData
}

interface OtherCity {
  slug: string
  city: string
  types: string[]
}

interface CityOverviewClientProps {
  city: string
  slug: string
  /** CMS city-level marketing copy; when empty a templated intro is used. */
  cityDescription: string | null
  clinics: DeepClinic[]
  otherCities: OtherCity[]
  faqs: {
    question: string
    answer: string
    slug?: string
    updatedAt?: string
    pages?: string[]
  }[]
  testimonials: Testimonial[]
}

// ─── Clinic type badge ────────────────────────────────────
// Same visual language as the locations index cards.

function ClinicTypeBadge({
  type,
  label,
}: {
  type: "mri" | "blood"
  label: string
}) {
  return (
    <span
      className="inline-block rounded-full px-2.5 py-1 text-sm font-medium"
      style={
        type === "mri"
          ? {
              backgroundColor: "var(--color-light-blue-100)",
              color: "var(--color-deep-blue-800)",
            }
          : {
              backgroundColor: "var(--color-error-100)",
              color: "#E96F3F", // ds-color-ignore: brand blood-test accent, matches locations index cards
            }
      }
    >
      {label}
    </span>
  )
}

// ─── Main component ───────────────────────────────────────

export function CityOverviewClient({
  city,
  slug,
  cityDescription,
  clinics,
  otherCities,
  faqs,
  testimonials,
}: CityOverviewClientProps) {
  const ut = useTranslations("ui")
  const t = useTranslations("locations")
  const locale = useLocale()
  const lp = useLocalePath()
  const country = useCountry().code
  const editLocale = cmsLocale(locale, country)

  const hasMri = clinics.some((c) => c.type === "mri")
  const hasBlood = clinics.some((c) => c.type === "blood")
  const multiClinic = clinics.length > 1

  // Keyword-led H1: the service the searcher typed ("blood test", "MRI") plus
  // the city — the query CARE ranks for and our old "{city}" H1 missed.
  const heroTitle =
    hasMri && hasBlood
      ? t("cp_cityTitleMriAndBlood", { city })
      : hasMri
        ? t("cp_cityTitleMri", { city })
        : t("cp_cityTitleBlood", { city })

  // City intro: authored CMS copy when present, else a templated line salted
  // with the real clinic names. The blood-only variant never promises "scans"
  // — the fallback must not over-promise imaging on blood-test-only cities.
  const clinicNames = clinics.map((c) => c.clinic).join(", ")
  const intro =
    cityDescription ??
    (hasMri
      ? t("cp_cityIntroFallback", { city, clinics: clinicNames })
      : t("cp_cityIntroFallbackBlood", { city, clinics: clinicNames }))

  return (
    <>
      {/* Breadcrumb */}
      <section className="px-6 pt-6">
        <div className="mx-auto max-w-[1100px]">
          <Link
            href={lp("/locations")}
            className="text-foreground inline-flex items-center gap-2 text-base transition-opacity hover:opacity-70"
          >
            <ChevronLeftIcon width={16} height={16} />
            {t("heroLabel")}
          </Link>
        </div>
      </section>

      {/* Hero */}
      <section className="px-6 pt-8 pb-4 md:pt-10">
        <div className="mx-auto max-w-[1100px]">
          <h1 className="type-display-lg text-foreground text-3xl font-normal sm:text-4xl md:leading-[1]">
            {heroTitle}
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-neutral-700">{intro}</p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <a href={bookingUrlForCountry(country, locale)}>
              <Button>{t("cp_bookCheckup")}</Button>
            </a>
            <Link href={lp("/how-it-works")}>
              <Button variant="outline">{t("cp_howItWorks")}</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Overview map */}
      <section className="px-6 pt-4 pb-8">
        <div className="mx-auto max-w-[1100px]">
          <SingleLocationMap
            markers={clinics.map((c) => ({
              lat: c.lat,
              lng: c.lng,
              type: c.type,
              label: c.clinic,
              address: c.address,
              href: lp(`/locations/${slug}/${c.clinicSlug}`),
            }))}
            center={{ lat: clinics[0].lat, lng: clinics[0].lng }}
          />
        </div>
      </section>

      {/* Clinics in this city — the deep content */}
      <section className="px-6 pb-16">
        <div className="mx-auto max-w-[1100px]">
          <h2 className="text-foreground text-2xl font-normal">
            {clinics.length === 1
              ? t("cp_clinicInCity", { city })
              : t("cp_clinicsInCity", { city })}
          </h2>

          {/* Jump-nav when there are several clinics in one city */}
          {multiClinic && (
            <nav
              aria-label={t("cp_clinicsInCity", { city })}
              className="mt-4 flex flex-wrap gap-2"
            >
              {clinics.map((c) => (
                <a
                  key={c.id}
                  href={`#clinic-${c.clinicSlug}`}
                  className="type-body-sm rounded-full border border-neutral-200 px-3 py-1.5 text-neutral-700 transition-colors hover:border-neutral-400"
                >
                  {c.clinic}
                </a>
              ))}
            </nav>
          )}

          <div className="mt-8 flex flex-col gap-14">
            {clinics.map((clinic) => (
              <article
                key={clinic.id}
                id={`clinic-${clinic.clinicSlug}`}
                className="scroll-mt-28 border-t border-neutral-200 pt-10 first:border-t-0 first:pt-0"
              >
                <div className="grid gap-8 md:grid-cols-[minmax(0,1fr)_320px]">
                  {/* Left: name, description, address/hours */}
                  <div>
                    <div className="mb-3">
                      <ClinicTypeBadge
                        type={clinic.type}
                        label={
                          clinic.type === "mri"
                            ? t("fullBodyCheckupBadge")
                            : t("bloodTestBadge")
                        }
                      />
                    </div>
                    <h3 className="text-foreground text-2xl font-medium">
                      {clinic.clinic}
                    </h3>
                    {clinic.detail.description && (
                      <EditableField
                        collection="locations"
                        id={clinic.id}
                        field="description"
                        locale={editLocale}
                        kind="scalar"
                        as="p"
                        className="mt-3 max-w-2xl text-base leading-relaxed text-neutral-700"
                      >
                        {clinic.detail.description}
                      </EditableField>
                    )}
                    {clinic.detail.languageNote && (
                      <ClinicLanguageNote
                        clinicId={clinic.id}
                        note={clinic.detail.languageNote}
                        className="mt-4"
                      />
                    )}
                    <ClinicAddressHours
                      clinicId={clinic.id}
                      address={clinic.address}
                      hours={clinic.detail.hours}
                      className="mt-6"
                    />
                  </div>

                  {/* Right: clinic photo */}
                  {clinic.image && (
                    <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[20px] md:aspect-auto md:h-full">
                      <Image
                        src={clinic.image}
                        alt={clinic.clinic}
                        fill
                        className="object-cover"
                        sizes="(max-width: 768px) 100vw, 320px"
                      />
                    </div>
                  )}
                </div>

                {/* Services / transport / parking — full width */}
                <ClinicServices
                  clinicId={clinic.id}
                  clinicType={clinic.type}
                  imagingServices={clinic.detail.imagingServices}
                  bloodServices={clinic.detail.bloodServices}
                  className="mt-10"
                />
                <ClinicTransport
                  clinicId={clinic.id}
                  publicTransport={clinic.detail.publicTransport}
                  className="mt-10"
                />
                <ClinicParking
                  clinicId={clinic.id}
                  parking={clinic.detail.parking}
                  className="mt-10"
                />

                {/* Deeplink to the focused clinic page (directions target) */}
                <Link
                  href={lp(`/locations/${slug}/${clinic.clinicSlug}`)}
                  className="text-aubergine-800 mt-8 inline-flex items-center gap-1.5 text-sm font-medium transition-opacity hover:opacity-70"
                >
                  {t("cp_fullDetails")}
                  <ArrowUpRightIcon size={14} />
                </Link>
              </article>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a href={bookingUrlForCountry(country, locale)}>
              <Button>{t("cp_bookCheckup")}</Button>
            </a>
            <Link
              href={lp("/what-we-detect")}
              className="type-body-md text-foreground inline-flex items-center gap-1 underline decoration-neutral-300 underline-offset-4 transition-opacity hover:opacity-70"
            >
              {t("cp_exploreWhatWeTest")}
              <ChevronRightIcon width={14} height={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Insurance reimbursement — the top Swiss purchase driver. Self-gates
          to markets where it applies (CH), so it's a safe drop-in. */}
      <InsuranceSection trackingLocation="locations-city-insurance" />

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="bg-neutral-50 px-6 py-16">
          <div className="mx-auto max-w-[1100px]">
            <TestimonialCardsRow
              customerCountLabel={t("cp_customerCount")}
              cmsTestimonials={testimonials}
            />
          </div>
        </section>
      )}

      {/* FAQ — dark section */}
      {faqs.length > 0 && (
        <FaqSection
          heading={t("cp_faqTitle")}
          items={faqs}
          variant="dark"
          linkLabel={null}
          currentPage={`locations/${slug}`}
        />
      )}

      {/* Other cities */}
      {otherCities.length > 0 && (
        <section className="border-t border-neutral-200 px-6 py-16">
          <div className="mx-auto max-w-[1100px]">
            <h2 className="text-foreground text-2xl font-normal sm:text-4xl md:text-5xl">
              {t("cp_otherLocations")}
            </h2>

            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {otherCities.map((loc) => (
                <Link
                  key={loc.slug}
                  href={lp(`/locations/${loc.slug}`)}
                  className="group block"
                >
                  <div className="rounded-[16px] bg-white p-5 transition-all duration-200 group-hover:-translate-y-px group-hover:shadow-md">
                    <h3 className="text-foreground text-lg font-medium transition-opacity group-hover:opacity-70">
                      {loc.city}
                    </h3>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {loc.types.includes("mri") && (
                        <span className="type-body-sm text-foreground font-mono tracking-wider uppercase">
                          {t("cp_mriCheckup")}
                        </span>
                      )}
                      {loc.types.includes("blood") && (
                        <span className="type-body-sm text-foreground font-mono tracking-wider uppercase">
                          {t("cp_bloodTestLabel")}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-8 text-center">
              <Link href={lp("/locations")}>
                <Button variant="outline">{ut("viewAllLocations")}</Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA banner — dark section */}
      <section className="bg-foreground px-6 py-20 text-center md:py-28">
        <h2 className="type-display-lg mx-auto max-w-2xl text-3xl font-normal text-white sm:text-4xl md:leading-[1]">
          {t("cp_readyToBook")}
        </h2>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <a href={bookingUrlForCountry(country, locale)}>
            <Button className="text-foreground bg-white hover:bg-white/90">
              {t("cp_bookCheckup")}
            </Button>
          </a>
          <Link href={lp("/contact")}>
            <Button variant="text" className="text-white hover:text-white/80">
              {t("cp_talkToTeam")}
            </Button>
          </Link>
        </div>
      </section>
    </>
  )
}
